import * as THREE from 'three';
import * as SceneModule from '../scene.js';
import { airplaneState } from '../airplane/state.js';
import { getAirplaneGroup } from '../airplane/create.js';
import { pointMaterial } from '../terrain/material.js';
import { params } from '../config.js';
// Lazy import for hunt mode payloads (to avoid circular dependency)
let revealPayloadsInRadiusFn = null;
let payloadsModulePromise = null;

function getRevealPayloadsFn() {
  return null;

  // If function already loaded, return it
  if (revealPayloadsInRadiusFn) {
    return revealPayloadsInRadiusFn;
  }
  
  // Start loading if not already loading
  if (!payloadsModulePromise) {
    payloadsModulePromise = import('../hunt/payloads.js').then(module => {
      revealPayloadsInRadiusFn = module.revealPayloadsInRadius;
      return revealPayloadsInRadiusFn;
    }).catch(() => {
      // Hunt mode not available
      return null;
    });
  }
  
  // Return null for now - will be available on next ping after module loads
  return null;
}

// Radar pulse state
export const radarState = {
  active: false,
  startTime: 0,
  fadeEndTime: 0,         // absolute time when effect should fully fade (can be extended)
  expandDuration: 1.6,    // seconds to expand to full radius
  persistDuration: 2.0,   // seconds to persist after expansion
  fadeDuration: 0.5,      // seconds to fade out
  maxRadius: 35.0,        // max expansion radius
  expandSpeed: 21.875,    // units per second (maxRadius / expandDuration)
  currentRadius: 0,       // current animated radius (doesn't reset on re-pulse)
  rainHeight: 41.0,       // how high particles start above their final position
  rainDuration: 0.45,     // how long the fall animation takes
  rainSpawnRadius: 5,     // how close to aircraft center particles originate
  showRing: false,        // whether to show the colored pulse ring
  ringFeather: 0.1,       // softness of the ring edges
  rainColor: '#9dc4cc',   // color of falling particles
  ringColorLow: '#00ccff',  // ring color for valleys
  ringColorMid: '#00ff80',  // ring color for mid heights
  ringColorHigh: '#ffee33', // ring color for peaks
  colorFadeDuration: 1.0,   // time to crossfade from rain to ring color
  // Visual ring settings
  visualRingEnabled: true,
  visualRingColor: '#ffa500',
  visualRingWidth: 0.07,
  visualRingOpacity: 1.0,
  // Rain particle shape
  rainParticleWidth: 0.01,
  rainParticleLength: 2.0,
  rainImpactScale: 4.0,
  // Persistent reveal tracking
  persistentRevealRadius: 0.0
};

// Multiple visual expanding rings - each pulse creates a new ring
const visualRings = [];
const MAX_VISUAL_RINGS = 10; // Pool size

// Active ring instances with their own animation state
const activeRings = [];

// Shared base geometry for all visual rings - created once, scaled via transform
let sharedRingGeometry = null;

function getSharedRingGeometry() {
  if (!sharedRingGeometry) {
    // Unit ring geometry (inner=0.9, outer=1.0) - we'll scale it to desired size
    sharedRingGeometry = new THREE.RingGeometry(0.9, 1.0, 128);
    sharedRingGeometry.rotateX(-Math.PI / 2); // Lay flat on XZ plane
  }
  return sharedRingGeometry;
}

function createVisualRingMesh() {
  const { scene } = SceneModule;
  
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: new THREE.Color(radarState.visualRingColor) },
      uOpacity: { value: radarState.visualRingOpacity },
      uFeather: { value: 0.05 }
    },
    vertexShader: `
      varying vec3 vLocalPos;
      void main() {
        vLocalPos = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 uColor;
      uniform float uOpacity;
      uniform float uFeather;
      varying vec3 vLocalPos;
      void main() {
        float dist = length(vLocalPos.xz);
        // Ring spans 0.9 to 1.0 in local space
        float innerAlpha = smoothstep(0.9 - uFeather, 0.9 + uFeather, dist);
        float outerAlpha = 1.0 - smoothstep(1.0 - uFeather, 1.0 + uFeather, dist);
        float alpha = innerAlpha * outerAlpha * uOpacity;
        float edgeDist = min(dist - 0.9, 1.0 - dist);
        float edgeGlow = 1.0 + 0.5 * (1.0 - smoothstep(0.0, uFeather * 2.0, edgeDist));
        gl_FragColor = vec4(uColor * edgeGlow, alpha);
      }
    `,
    transparent: true,
    side: THREE.DoubleSide,
    depthWrite: false,
    blending: THREE.AdditiveBlending
  });
  
  const mesh = new THREE.Mesh(getSharedRingGeometry(), material);
  mesh.visible = false;
  mesh.renderOrder = 999;
  scene.add(mesh);
  return mesh;
}

function spawnVisualRing(originX, originY, originZ) {
  if (!radarState.visualRingEnabled) return;

  // Get or create a ring mesh
  let ring;
  if (visualRings.length < MAX_VISUAL_RINGS) {
    ring = createVisualRingMesh();
    visualRings.push(ring);
  } else {
    // Find an inactive ring to reuse
    ring = visualRings.find(r => !r.visible);
    if (!ring) {
      // All rings in use, reuse the oldest active one
      const oldest = activeRings.shift();
      if (oldest) {
        oldest.mesh.visible = false;
        ring = oldest.mesh;
      } else {
        return; // No available rings
      }
    }
  }
  
  ring.position.set(originX, originY, originZ);
  ring.scale.set(0.01, 1, 0.01); // Start small
  ring.visible = true;

  activeRings.push({
    mesh: ring,
    startTime: performance.now() * 0.001
  });
}

export function updateVisualRings() {
  const now = performance.now() * 0.001;
  // Visual ring fades out over 2 seconds total, starting immediately
  const visualRingLifetime = 2.0;

  for (let i = activeRings.length - 1; i >= 0; i--) {
    const ringData = activeRings[i];
    const elapsed = now - ringData.startTime;

    if (elapsed >= visualRingLifetime) {
      // Ring is done, hide it
      ringData.mesh.visible = false;
      activeRings.splice(i, 1);
      continue;
    }

    // Calculate radius - expand over the full lifetime
    const expandProgress = Math.min(elapsed / radarState.expandDuration, 1.0);
    const currentRadius = expandProgress * radarState.maxRadius;
    
    // Fade out gradually over the lifetime (starts fading immediately)
    // Use an eased fade that starts slow and accelerates
    const lifeProgress = elapsed / visualRingLifetime;
    const currentFade = 1.0 - (lifeProgress * lifeProgress); // Quadratic ease-out
    
    // Scale the ring to desired size (base geometry is unit ring 0.9-1.0)
    // To get ring with width W centered at radius R, we scale by R + W
    // The ring will span from (R+W)*0.9 to (R+W)*1.0
    const width = radarState.visualRingWidth;
    const ringScale = Math.max(0.01, currentRadius + width);
    ringData.mesh.scale.set(ringScale, 1, ringScale);
    
    const mat = ringData.mesh.material;
    mat.uniforms.uOpacity.value = radarState.visualRingOpacity * currentFade;
    mat.uniforms.uColor.value.set(radarState.visualRingColor);
    // Feather needs to be in local space (divided by scale)
    mat.uniforms.uFeather.value = (width * 0.3) / ringScale;
  }
}

// Reusable vector for radar origin calculations
const _radarOrigin = new THREE.Vector3();

export function triggerRadarPulse() {
  const { camera } = SceneModule;
  
  radarState.maxRadius = 35;
  
  // Get origin from airplane position or camera position
  const airplaneGroup = getAirplaneGroup();
  if (airplaneGroup && airplaneState.enabled) {
    // Use the airplane's actual world position (includes strafe offset)
    _radarOrigin.copy(airplaneState.worldPos);
  } else {
    _radarOrigin.copy(camera.position);
    _radarOrigin.y = 0;
  }

  // Always spawn a new visual ring (multiple rings supported)
  spawnVisualRing(_radarOrigin.x, _radarOrigin.y, _radarOrigin.z);
  
  // Reveal payloads (disabled)
  if (false) {
    const revealFn = getRevealPayloadsFn();
    if (revealFn) {
      revealFn(_radarOrigin, radarState.maxRadius);
    } else if (payloadsModulePromise) {
      // Module is loading, wait for it and spawn on next frame
      payloadsModulePromise.then(fn => {
        if (fn) fn(_radarOrigin, radarState.maxRadius);
      });
    }
  }
  
  const now = performance.now() * 0.001;
  radarState.expandSpeed = radarState.maxRadius / radarState.expandDuration;
  const totalDuration = radarState.expandDuration + radarState.persistDuration + radarState.fadeDuration;

  if (radarState.active) {
    // ADDITIVE: Already active - extend the timer, don't reset animation
    // Extend fade end time from now
    radarState.fadeEndTime = now + radarState.persistDuration + radarState.fadeDuration;
    // Keep fade at 1.0 (refresh the timer)
    pointMaterial.uniforms.uRadarFade.value = 1.0;
    // DON'T reset: startTime, uRadarTime, uRadarRadius, currentRadius
    // The ring continues expanding from where it was
  } else {
    // Fresh start - no active pulse
    radarState.active = true;
    radarState.startTime = now;
    radarState.fadeEndTime = now + totalDuration;
    radarState.currentRadius = 0;

    pointMaterial.uniforms.uRadarRadius.value = 0.0;
    pointMaterial.uniforms.uRadarFade.value = 1.0;
    pointMaterial.uniforms.uRadarTime.value = 0.0;
    pointMaterial.uniforms.uRadarOrigin.value.copy(_radarOrigin);
  }
  
  const u = pointMaterial.uniforms;
  u.uRadarActive.value = 1.0;
  u.uRadarRainHeight.value = radarState.rainHeight;
  u.uRadarRainDuration.value = radarState.rainDuration;
  u.uRainSpawnRadius.value = radarState.rainSpawnRadius;
  u.uRadarExpandSpeed.value = radarState.expandSpeed;
  // Show ring if enabled OR if permanent radar mode is on (rKeyState === 2)
  u.uRadarShowRing.value = (radarState.showRing || params.rKeyState === 2) ? 1.0 : 0.0;
  u.uRadarRingFeather.value = radarState.ringFeather;
  u.uRainColor.value.set(radarState.rainColor);
  u.uRadarRingColorLow.value.set(radarState.ringColorLow);
  u.uRadarRingColorMid.value.set(radarState.ringColorMid);
  u.uRadarRingColorHigh.value.set(radarState.ringColorHigh);
  u.uRainColorFadeDuration.value = radarState.colorFadeDuration;
  u.uRainParticleWidth.value = radarState.rainParticleWidth;
  u.uRainParticleLength.value = radarState.rainParticleLength;
  u.uRainImpactScale.value = radarState.rainImpactScale;
}

export function updateRadarPulse() {
  if (!radarState.active) return;

  const now = performance.now() * 0.001;
  const elapsed = now - radarState.startTime;

  // Update time uniform for rain animation
  pointMaterial.uniforms.uRadarTime.value = elapsed;

  // Ring expansion: grows until maxRadius, then stays there
  // This is independent of fade timing, so re-pulsing doesn't reset the ring
  if (radarState.currentRadius < radarState.maxRadius) {
    radarState.currentRadius = Math.min(elapsed * radarState.expandSpeed, radarState.maxRadius);
  }

  // Fade timing based on fadeEndTime (which can be extended by re-pulsing)
  const fadeStartTime = radarState.fadeEndTime - radarState.fadeDuration;
  let currentFade = 1.0;

  if (now >= radarState.fadeEndTime) {
    // Done - fully faded
    radarState.active = false;
    radarState.currentRadius = 0;
    radarState.persistentRevealRadius = 0.0;
    pointMaterial.uniforms.uRadarActive.value = 0.0;
    pointMaterial.uniforms.uRadarFade.value = 0.0;
    pointMaterial.uniforms.uPersistentRevealRadius.value = 0.0;
  } else if (now >= fadeStartTime) {
    // Fade out phase
    const fadeProgress = (now - fadeStartTime) / radarState.fadeDuration;
    currentFade = 1.0 - fadeProgress;
    pointMaterial.uniforms.uRadarFade.value = currentFade;
  } else {
    // Still in expand or persist phase - full visibility
    currentFade = 1.0;
    pointMaterial.uniforms.uRadarFade.value = 1.0;
  }

  // Update radius uniform
  pointMaterial.uniforms.uRadarRadius.value = radarState.currentRadius;

  // Update persistent reveal radius (only grows, never shrinks)
  radarState.persistentRevealRadius = Math.max(radarState.persistentRevealRadius, radarState.currentRadius);
  pointMaterial.uniforms.uPersistentRevealRadius.value = radarState.persistentRevealRadius;

  // Update rain particle shape uniforms in real-time
  pointMaterial.uniforms.uRainParticleWidth.value = radarState.rainParticleWidth;
  pointMaterial.uniforms.uRainParticleLength.value = radarState.rainParticleLength;
  pointMaterial.uniforms.uRainImpactScale.value = radarState.rainImpactScale;
}
